import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogsAPI, servicesAPI, jobsAPI, settingsAPI } from '../services/api';
import { Blog, Service, Job } from '../types';
import { FadeIn, SlideIn, ScaleIn, Stagger, StaggerItem, HoverScale } from '../components/animations';
import { Briefcase, GraduationCap, TrendingUp, Users, MapPin, Clock, DollarSign, Sparkles } from 'lucide-react';
import { Card, CardBody, Badge } from '../components/ui';

const Home: React.FC = () => {
  const [trendingBlogs, setTrendingBlogs] = useState<Blog[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [featuredJobs, setFeaturedJobs] = useState<Job[]>([]);
  const [heroImages, setHeroImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [blogsRes, servicesRes, jobsRes, settingsRes] = await Promise.all([
        blogsAPI.getTrending(),
        servicesAPI.getAll({ isActive: true }),
        jobsAPI.getAll({ featured: true, limit: 6 }),
        settingsAPI.getAll({ category: 'hero' })
      ]);
      
      // Safely extract data with fallbacks
      const blogs = blogsRes?.data?.data || [];
      const services = servicesRes?.data?.data || [];
      const jobs = jobsRes?.data?.data || [];
      const settings = settingsRes?.data?.data || {};
      
      setTrendingBlogs(Array.isArray(blogs) ? blogs : []);
      setServices(Array.isArray(services) ? services.slice(0, 3) : []);
      setFeaturedJobs(Array.isArray(jobs) ? jobs : []);
      
      // Extract hero images from settings
      const images = typeof settings === 'object' ? Object.keys(settings)
        .filter(key => key.startsWith('hero_image_'))
        .sort()
        .map(key => settings[key])
        .filter(url => url) : [];
      
      setHeroImages(images.length > 0 ? images : [
        'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1920',
        'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1920',
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920',
        'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1920'
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Set empty arrays on error instead of crashing
      setTrendingBlogs([]);
      setServices([]);
      setFeaturedJobs([]);
      setHeroImages([
        'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1920',
        'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1920',
        'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920',
        'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1920'
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section with Auto-Sliding Background */}
      <section className="relative text-white py-32 overflow-hidden">
        {/* Auto-sliding background images */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
          <div className="hero-slider">
            {heroImages.map((image, index) => (
              <div 
                key={index} 
                className="slide" 
                style={{ 
                  backgroundImage: `url(${image})`,
                  animationDelay: `${index * 5}s`
                }}
              ></div>
            ))}
          </div>
        </div>
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-semibold mb-6">
              🌍 Africa's #1 Career Platform
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Your Dream Career <br/>
              <span className="text-yellow-400">Starts Here</span>
            </h1>
            
            <p className="text-xl mb-8 text-gray-200 leading-relaxed">
              Connect with top employers, discover scholarships, and unlock opportunities across Africa. We're here to help you succeed.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/jobs">
                <button className="px-8 py-4 bg-yellow-400 text-gray-900 rounded-lg font-semibold hover:bg-yellow-300 transition shadow-lg">
                  Find Jobs
                </button>
              </Link>
              <Link to="/opportunities">
                <button className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold border-2 border-white/30 hover:bg-white/20 transition">
                  Explore Scholarships
                </button>
              </Link>
            </div>
            
            <div className="mt-12 flex items-center gap-8 text-sm">
              <div>
                <div className="text-3xl font-bold">10,000+</div>
                <div className="text-gray-300">Active Users</div>
              </div>
              <div>
                <div className="text-3xl font-bold">500+</div>
                <div className="text-gray-300">Companies</div>
              </div>
              <div>
                <div className="text-3xl font-bold">200+</div>
                <div className="text-gray-300">Scholarships</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Articles */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Latest Career Insights</h2>
              <p className="text-gray-600 mt-1">Tips and advice from industry experts</p>
            </div>
            <Link to="/blogs" className="text-blue-600 hover:text-blue-700 font-medium">
              See all articles →
            </Link>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {trendingBlogs.slice(0, 3).map(blog => (
                <Link key={blog._id} to={`/blogs/${blog.slug}`} className="group">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition">
                    {blog.featuredImage && (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={blog.featuredImage}
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition">
                        {blog.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-3">{blog.excerpt}</p>
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(blog.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">How We Can Help You</h2>
            <p className="text-lg text-gray-600">Professional services designed for your success</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map(service => (
              <div key={service._id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <DollarSign className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.name}</h3>
                <div className="text-2xl font-bold text-blue-600 mb-3">${service.price}</div>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <Link to="/services">
                  <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                    Learn More
                  </button>
                </Link>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/services" className="text-blue-600 hover:text-blue-700 font-medium">
              View all services →
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Jobs */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Featured Opportunities</h2>
              <p className="text-gray-600 mt-1">Hand-picked jobs from top companies</p>
            </div>
            <Link to="/jobs" className="text-blue-600 hover:text-blue-700 font-medium">
              Browse all jobs →
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredJobs.map(job => (
              <Link key={job._id} to={`/jobs/${job._id}`} className="group">
                <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-lg transition">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">Featured</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition">
                    {job.title}
                  </h3>
                  <p className="text-gray-600 mb-3">{job.business.companyName}</p>
                  <div className="space-y-1 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {job.location.city}, {job.location.country}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {job.employmentType}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Take the Next Step?</h2>
          <p className="text-xl mb-8 text-blue-100">Join thousands of professionals building their careers with us</p>
          <Link to="/register">
            <button className="px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition shadow-lg">
              Create Free Account
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
